import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

export type VoiceAudioTelemetryEvent =
  | "voice.call_audio_summary"
  | "voice.inbound_audio_stalled"
  | "voice.playback_blocked"
  | "voice.playback_failed";

export type VoiceAudioTelemetryContext = Readonly<
  Record<string, string | number | boolean | null>
>;

export function logVoiceAudioEvent(
  event: VoiceAudioTelemetryEvent,
  context: VoiceAudioTelemetryContext,
): void {
  try {
    datadogLogs.logger.info(event, context);
    datadogRum.addAction(event, context);
  } catch {
    // Telemetry must never interrupt a live call.
  }
}

/** No new inbound RTP packets for this long, after the first one, is a stall. */
export const INBOUND_STALL_MS = 5000;

/** Cumulative counters from one peer connection's audio `inbound-rtp` stats. */
export interface InboundAudioTotals {
  readonly packetsReceived: number;
  readonly packetsLost: number;
  readonly totalAudioEnergy: number;
  readonly totalSamplesDuration: number;
}

export interface InboundAudioSample extends InboundAudioTotals {
  readonly atMs: number;
}

export interface InboundAudioState {
  readonly first: InboundAudioSample | null;
  readonly last: InboundAudioSample | null;
  /** Totals from earlier peer connections of the same call. */
  readonly carried: InboundAudioTotals;
  readonly lastPacketAtMs: number | null;
  readonly longestStallMs: number;
  readonly stallReported: boolean;
}

const zeroTotals: InboundAudioTotals = {
  packetsReceived: 0,
  packetsLost: 0,
  totalAudioEnergy: 0,
  totalSamplesDuration: 0,
};

const addTotals = (
  a: InboundAudioTotals,
  b: InboundAudioTotals,
): InboundAudioTotals => ({
  packetsReceived: a.packetsReceived + b.packetsReceived,
  packetsLost: a.packetsLost + b.packetsLost,
  totalAudioEnergy: a.totalAudioEnergy + b.totalAudioEnergy,
  totalSamplesDuration: a.totalSamplesDuration + b.totalSamplesDuration,
});

export const initialInboundAudioState: InboundAudioState = {
  first: null,
  last: null,
  carried: zeroTotals,
  lastPacketAtMs: null,
  longestStallMs: 0,
  stallReported: false,
};

/** `stallStarted` fires once per stall; counters dropping mean a new peer connection. */
export function recordInboundAudioSample(
  state: InboundAudioState,
  sample: InboundAudioSample,
): { readonly state: InboundAudioState; readonly stallStarted: boolean } {
  const restarted =
    state.last != null && sample.packetsReceived < state.last.packetsReceived;
  const carried =
    restarted && state.last != null
      ? addTotals(state.carried, state.last)
      : state.carried;
  const previousPackets = restarted ? 0 : (state.last?.packetsReceived ?? 0);
  const receivedNewPackets = sample.packetsReceived > previousPackets;
  const lastPacketAtMs = receivedNewPackets
    ? sample.atMs
    : state.lastPacketAtMs;
  const stallMs = lastPacketAtMs == null ? 0 : sample.atMs - lastPacketAtMs;
  const stallReported = receivedNewPackets ? false : state.stallReported;
  const stallStarted = !stallReported && stallMs >= INBOUND_STALL_MS;
  return {
    state: {
      first: state.first ?? sample,
      last: sample,
      carried,
      lastPacketAtMs,
      longestStallMs: Math.max(state.longestStallMs, stallMs),
      stallReported: stallReported || stallStarted,
    },
    stallStarted,
  };
}

export function summarizeInboundAudio(
  state: InboundAudioState,
): VoiceAudioTelemetryContext {
  const { first, last } = state;
  const totals = last == null ? state.carried : addTotals(state.carried, last);
  return {
    sampledSeconds:
      first != null && last != null
        ? Math.round((last.atMs - first.atMs) / 1000)
        : 0,
    inboundPacketsReceived: totals.packetsReceived,
    inboundPacketsLost: totals.packetsLost,
    inboundAudioEnergy: totals.totalAudioEnergy,
    inboundAudioSeconds: Math.round(totals.totalSamplesDuration),
    longestInboundStallSeconds: Math.round(state.longestStallMs / 1000),
  };
}

/** Reads the audio `inbound-rtp` entry from an `RTCPeerConnection` stats report. */
export function readInboundAudioSample(
  report: RTCStatsReport,
  atMs: number,
): InboundAudioSample | null {
  for (const stat of report.values()) {
    if (isInboundAudioStat(stat)) {
      return {
        atMs,
        packetsReceived: stat.packetsReceived ?? 0,
        packetsLost: stat.packetsLost ?? 0,
        totalAudioEnergy: stat.totalAudioEnergy ?? 0,
        totalSamplesDuration: stat.totalSamplesDuration ?? 0,
      };
    }
  }
  return null;
}

function isInboundAudioStat(stat: unknown): stat is RTCInboundRtpStreamStats {
  return (
    typeof stat === "object" &&
    stat != null &&
    "type" in stat &&
    stat.type === "inbound-rtp" &&
    "kind" in stat &&
    stat.kind === "audio"
  );
}
